<template>
  <div class="chat" :class="{'chat--dense': dense, 'chat--new': activeChat.isNew}">
    <div v-if="!dense" class="chat__top">
      <c-btn-back @click="$emit('close')" />
      <div class="chat__top__content">
        <template v-if="account.role === 'CLINIC_ADMIN'">
          <div class="chat-members">
            <div class="chat-members__top">
              <template v-for="(member, index) in activeChat.members">
                {{ member.fullName }}<template v-if="index < activeChat.members.length-1">, </template>
              </template>
            </div>
            <div class="chat-members__bottom">
              {{ activeChat.users.length }} {{ $t('chat.participants') }}
              <div class="chat-members__divider"></div>
              <template v-for="(member, index) in activeChat.members">
                {{ $t(member.roleName) }}<template v-if="index < activeChat.members.length-1">, </template>
              </template>
            </div>
          </div>
        </template>
        <template v-else>
          <template v-if="activeChat.members.length === 1 || $vuetify.breakpoint.smAndUp">
            <div v-for="member in activeChat.members" :key="member.id" class="chat-account">
              <div class="chat-account__avatar">
                <v-img
                  :src="member.profilePicture || require('@/assets/images/default-avatar.png')"
                  @error="member.profilePicture = require('@/assets/images/default-avatar.png')" />
              </div>
              <div class="chat-account__content">
                <span class="chat-account__name">{{ member.fullName }}</span>
                <span class="chat-account__role">{{ $t(member.roleName) }}</span>
              </div>
            </div>
          </template>
          <template v-if="activeChat.members.length > 1 && $vuetify.breakpoint.xs">
            <div class="chat-members">
              <div class="chat-members__top">
                <template v-for="(member, index) in activeChat.members">
                  {{ member.fullName }}<template v-if="index < activeChat.members.length-1">, </template>
                </template>
              </div>
              <div class="chat-members__bottom">
                <template v-for="(member, index) in activeChat.members">
                  {{ $t(member.roleName) }}<template v-if="index < activeChat.members.length-1">, </template>
                </template>
              </div>
            </div>
          </template>
        </template>
      </div>
      <div class="chat__top__actions">
        <!-- TO DO finish the logic for adding a third participant to the chat -->
        <!-- <v-btn
          v-if="!activeChat.isNew && account.role === 'CLINIC_ADMIN'"
          class="v-btn--text-bg"
          color="primary"
          text
          fab
          @click="openDialog">
          <v-icon>$account-plus</v-icon>
        </v-btn> -->
        <template v-if="account.role === 'PROVIDER' && !activeChat.isNew">
          <v-menu v-model="menu" :close-on-content-click="false" nudge-bottom="10" offset-y left>
            <template #activator="{ on }">
              <v-btn :loading="loading" class="primary--text" color="white" fab v-on="on">
                <v-icon class="chat__top__actions--dots" color="primary" size="18">$dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item :disabled="!clinicAdmin" @click="removeClinicAdmin">{{ $t('chat.removeAdmin') }}</v-list-item>
              <v-list-item :disabled="!canDisablePatient" @click="disablePatient">{{ $t('chat.disablePatient') }}</v-list-item>
            </v-list>
          </v-menu>
        </template>
      </div>
    </div>
    <messages-list
      v-chat-scroll
      :items="chat.messages"
      :loading="messagesLoading"
      :dense="dense"
      @onScrollTop="getMessages" />
    <div class="chat__input" :class="{'chat__input--disabled': chat.disabled}">
      <div class="chat__input__wrapper">
        <!--        <v-btn class="v-btn&#45;&#45;text-bg" large icon>-->
        <!--          <v-icon>$upload</v-icon>-->
        <!--        </v-btn>-->
        <label>
          <textarea
            v-model.trim="newMessage"
            :placeholder="$t('chat.typeMessage')"
            :disabled="chat.disabled"
            @keydown.enter.exact.prevent
            @keyup.enter.exact="sendMessage"></textarea>
        </label>
        <div class="chat__input__action">
          <v-btn
            :loading="sendMessageLoading"
            :disabled="chat.disabled"
            class="v-btn--text-bg"
            color="primary"
            large
            icon
            @click="sendMessage">
            <v-icon>$send-message</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { Chat } from '@/common/modules/Chat';

import CBtnBack from '@/components/ui/CBtnBack';
import MessagesList from '@/components/lists/MessagesList';

export default {
  name: 'Chat',
  components: { CBtnBack, MessagesList },
  props: {
    dense: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chat: null,
      menu: false,
      loading: false,
      newMessage: '',
      queryParams: {
        page: -1,
        pageSize: 50,
      },
      messagesLoading: false,
      sendMessageLoading: false,
      patient: null,
      clinicAdmin: null,
      canDisablePatient: false,
    };
  },
  computed: {
    ...mapState('loggedAccount', ['account']),
    ...mapState('messenger', ['activeChat']),
  },
  created() {
    this.chat = new Chat(this.activeChat);
    if (!this.activeChat.isNew) this.getMessages();
    window.onbeforeunload = () => this.chat && this.chat.off();

    if (this.account.role === 'PROVIDER') {
      this.$watch('activeChat', chat => {
        if (chat) {
          this.patient = chat.members.find(user => user.role === 'PATIENT');
          this.clinicAdmin = chat.members.find(user => user.role === 'CLINIC_ADMIN');
          this.canDisablePatient = this.patient && !chat.disabledForPatient;
        } else {
          this.patient = null;
          this.clinicAdmin = null;
          this.canDisablePatient = false;
        }
      }, { deep: true, immediate: true });
    }
  },
  beforeDestroy() {
    this.chat.off();
  },
  methods: {
    getMessages($state = {}) {
      this.messagesLoading = true;
      this.queryParams.page++;

      this.chat.getMessages(this.queryParams).then(total => {
        if (total < (this.queryParams.pageSize * this.queryParams.page) && 'completed' in $state) $state.completed();
        if ('context' in $state) $state.context.isLoading = false;
      }).finally(() => this.messagesLoading = false);
    },
    sendMessage() {
      if (this.newMessage) {
        this.sendMessageLoading = true;
        this.chat.sendMessage(this.newMessage)
          .catch(console.dir)
          .finally(() => {
            this.newMessage = '';
            this.sendMessageLoading = false;
          });
      }
    },
    removeClinicAdmin() {
      this.$sockets.chat.emit('remove-clinic-admin', { roomId: this.activeChat.id, clinicAdminId: this.clinicAdmin.id });
      this.updateChat();
    },
    disablePatient() {
      this.$openDialog({
        component: 'confirmation',
        params: { width: 450 },
        props: {
          title: this.$t('confirmation'),
          text: 'Are you sure want to disable chat for patient ?',
          showCancel: true,
        },
      }).then(() => {
        this.$sockets.chat.emit('disable-patient', { roomId: this.activeChat.id, patientId: this.patient.id });
        this.updateChat();
      });
    },
    updateChat() {
      this.menu = false;
      this.loading = true;
      Promise.allSettled([
        this.$store.dispatch('messenger/getChats'),
        this.$store.dispatch('messenger/updateActiveChat', this.activeChat.id),
      ]).catch(console.dir).finally(() => this.loading = false);
    },
    openDialog() {
      this.$openDialog({
        component: 'messages-accounts',
        params: {
          width: 500,
        },
      }).then(account => {
        console.dir(account);
      });
    },
  },
};
</script>

<style lang="scss">
 @import "../../assets/style/shared/Chat";
</style>
